import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { FirebaseAppContext } from 'libs.firebase_react';
import { ModalWindow, Spinner } from 'libs.nucleus';
import { AuthContext } from 'libs.react_internal';
import { DataReportsContext } from '../../../../contexts/data_reports/data_reports.context';
import { ReportAnalyticsEvents } from '../../../constants/report_analytics_events.constants';
import { usePaginatedCubeQuery } from '../../../hooks/use_paginated_cube_query';
import { useServerSideCubeQuery } from '../../../hooks/use_server_side_cube_query';
import { ChartContent } from '../report_chart_content.component';
export const DrilldownModal = ({ title, onClose, query, chartType, showAnnotations, disablePreAggs, }) => {
    // Drilldown mirrors the parent report: legacy reports (no `rid`) render TIME
    // dimensions as date-only, new-flow reports render full timestamps (SSP-4116).
    const { setDrilldownLevel, studyId, showFullTimestamp } = useContext(DataReportsContext);
    const { logEvent } = useContext(FirebaseAppContext);
    const { entityId, user } = useContext(AuthContext);
    // Server-side pagination state for table charts
    const [paginationState, setPaginationState] = useState({
        pageIndex: 0,
        pageSize: 5,
    });
    const isTableChart = chartType === 'table';
    const logDrilldownEvent = useCallback((eventName, eventData) => {
        logEvent(eventName, {
            study_id: studyId,
            entity_id: entityId,
            account_id: user?.path ?? user?.id,
            chart_type: chartType,
            ...eventData,
        });
    }, [logEvent, studyId, entityId, user?.path, user?.id, chartType]);
    const handleDrilldownQueryExecuted = useCallback(() => {
        logDrilldownEvent(ReportAnalyticsEvents.QUERY_EXECUTED, {
            query_source: 'drilldown',
        });
    }, [logDrilldownEvent]);
    // Memoize the query to prevent infinite re-renders
    const memoizedQuery = useMemo(() => {
        if (!query) {
            return undefined;
        }
        return { ...query, renewQuery: true, total: true };
    }, [query]);
    // Use server-side pagination for table charts, full pagination for other chart types
    const paginatedResult = usePaginatedCubeQuery(memoizedQuery, {
        skip: !query || isTableChart,
        disablePreAggs,
        onQueryExecuted: handleDrilldownQueryExecuted,
    });
    const serverSideResult = useServerSideCubeQuery(memoizedQuery, {
        skip: !query || !isTableChart,
        disablePreAggs,
        pagination: paginationState,
        onQueryExecuted: handleDrilldownQueryExecuted,
    });
    // Select the appropriate result based on chart type
    const resultSets = isTableChart
        ? serverSideResult.resultSet
            ? [serverSideResult.resultSet]
            : null
        : paginatedResult.resultSets;
    const isLoading = isTableChart ? serverSideResult.isLoading : paginatedResult.isLoading;
    const error = isTableChart ? serverSideResult.error : paginatedResult.error;
    const totalRows = serverSideResult.total;
    // Reset pagination when query changes (only for table charts)
    useEffect(() => {
        if (isTableChart) {
            setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
        }
    }, [memoizedQuery, isTableChart]);
    // Analytics: fire drilldown data loaded when results arrive
    useEffect(() => {
        if (!isLoading && resultSets && resultSets.length > 0) {
            logDrilldownEvent(ReportAnalyticsEvents.DRILLDOWN_DATA_LOADED);
        }
    }, [isLoading, resultSets]);
    // Handler for pagination changes from TableChart
    const handlePaginationChange = (newPagination) => {
        setPaginationState(newPagination);
    };
    const handleClose = () => {
        setDrilldownLevel((prev) => prev - 1);
        onClose();
    };
    return (_jsx(ModalWindow, { title: title, isOpen: true, closeWindow: handleClose, hideCloseIcon: false, width: 'lg', children: _jsx("div", { className: 'min-h-[300px] flex', children: isLoading ? (_jsx("div", { className: 'flex items-center justify-center size-full min-h-[300px]', children: _jsx(Spinner, { size: 'md' }) })) : error ? (_jsxs("div", { children: ["Error -- ", error.message] })) : !resultSets || resultSets.length === 0 || resultSets.flatMap((rs) => rs?.rawData() || []).length === 0 ? (_jsx("div", { children: "No data available" })) : (_jsx("div", { className: 'flex flex-row flex-1 overflow-auto', children: _jsx(ChartContent, { isLoading: isLoading, chartError: error, resultSets: resultSets, chartType: chartType, showAnnotations: showAnnotations, serverSidePagination: isTableChart
                        ? {
                            total: totalRows,
                            paginationState,
                            onPaginationChange: handlePaginationChange,
                        }
                        : undefined, showFullTimestamp: showFullTimestamp }) })) }) }));
};
